import React, {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  MATCH_RULE_TYPE,
  MATCH_RULE_TYPE_STR,
  MatchRuleEntity,
  useCreateMatchRuleMutation,
  useUpdateMatchRuleMutation,
  useDeleteMatchRuleMutation,
} from 'src/store/matchRuleApi';
import {
  selectMatchRuleCount,
  setMatchRule as setMatchRuleAction,
} from 'src/store/matchRuleSlice';
import _ from 'lodash';
import InterviewCollectSelector from 'src/component/interviewCollectSelector';
import ProfileFieldSelector from 'src/component/profileFiledSelector';
import { Panel } from 'src/ui/Panel';
import { Button } from 'src/ui/Button';
import { Icon } from 'src/ui/Icon';
import { Field } from 'src/ui/Field';
import { Input } from 'src/ui/Input';
import { Select } from 'src/ui/Select';
import { Textarea } from 'src/ui/Textarea';
import { Switch } from 'src/ui/Switch';
import { useToast } from 'src/ui/Toast';

export interface EditorRef {
  onCreate: () => void;
  onSelect: (dbMatchRule: MatchRuleEntity) => void;
}

/*
  1. Perfect Match – Ideal compatibility.
  2. Great Match – Very compatible with minor differences.
  3. Good Match – Generally compatible, with some differences.
  4. Fair Match – Compatible but with noticeable differences.
  5. Mixed Match – Some areas of strong compatibility, others with significant differences.
  6. Weak Match – Many differences, with few areas of compatibility.
  7. Poor Match – Significant differences that could lead to conflict.
  8. Deal Breaker – Incompatible to the point that the relationship may not be viable.
*/

const blankMatchRule = (matchRuleCount: number): MatchRuleEntity => ({
  id: 0,
  sequence: matchRuleCount + 1,
  name: '',
  type: MATCH_RULE_TYPE.AI,
  disabled: false,
  fields: [],
  weightMale: 100,
  weightFemale: 100,
  prefills: [],
  grading: [
    ['perfect_match', ''],
    ['good_match', ''],
    ['fair_match', ''],
    ['poor_match', ''],
    ['deal_breaker', ''],
  ],
  prompt: '',
});

export interface EditorProps {
  refetchAll: () => void;
  onTestMatchRule: () => void;
}

const Editor = forwardRef(
  (
    { refetchAll, onTestMatchRule }: EditorProps,
    ref: ForwardedRef<EditorRef>
  ) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const matchRuleCount = useSelector(selectMatchRuleCount);
    const [matchRule, setMatchRule] = useState<MatchRuleEntity>(
      blankMatchRule(matchRuleCount)
    );
    const isDirty = useRef(false);
    const language = 'cn';
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [createMatchRule, { isLoading: isCreating }] =
      useCreateMatchRuleMutation();
    const [updateMatchRule, { isLoading: isUpdating }] =
      useUpdateMatchRuleMutation();
    const [deleteMatchRule, { isLoading: isDeleting }] =
      useDeleteMatchRuleMutation();

    const checkCanChange = useCallback(() => {
      if (isDirty.current) {
        const confirmed = window.confirm(
          'You have unsaved changes. Are you sure you want to discard them?'
        );
        if (confirmed) {
          isDirty.current = false;
          return true;
        }
        return false;
      }
      return true;
    }, []);

    const onCreate = () => {
      if (checkCanChange()) {
        setMatchRule(blankMatchRule(matchRuleCount));
      }
    };

    const onDuplicate = () => {
      setMatchRule({
        ...matchRule,
        id: 0,
        sequence: matchRuleCount + 1,
        name: `${matchRule.name}_copy`,
      });
    };

    useImperativeHandle(ref, () => ({
      onCreate,
      onSelect: (dbMatchRule: MatchRuleEntity) => {
        if (checkCanChange()) {
          setMatchRule(_.cloneDeep(dbMatchRule));
        }
      },
    }));

    const onChange = (
      key: string,
      value: string | number | boolean | undefined
    ) => {
      isDirty.current = true;
      const newMatchRule: any = { ...matchRule };
      const keys = key.split('.');
      if (keys.length === 1) {
        newMatchRule[key] = value;
      } else {
        newMatchRule[keys[0]][keys[1]] = value;
      }
      setMatchRule(newMatchRule);
    };

    const onChangeField = (idx: number, id: number, field = '') => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      if (!newMatchRule.fields) newMatchRule.fields = [];
      newMatchRule.fields[idx] = [id, field];
      setMatchRule(newMatchRule);
    };

    const onDeleteField = (idx: number) => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      if (!newMatchRule.fields) newMatchRule.fields = [];
      newMatchRule.fields.splice(idx, 1);
      setMatchRule(newMatchRule);
    };

    const onChangeGrading = (idx: number, grade: string, description = '') => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      if (!newMatchRule.grading) newMatchRule.grading = [];
      newMatchRule.grading[idx] = [grade, description];
      setMatchRule(newMatchRule);
    };

    const onDeleteGrading = (idx: number) => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      if (!newMatchRule.grading) newMatchRule.grading = [];
      newMatchRule.grading.splice(idx, 1);
      setMatchRule(newMatchRule);
    };

    const onChangePrefill = (idx: number, field: string) => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      newMatchRule.prefills[idx] = field;
      setMatchRule(newMatchRule);
    };

    const onDeletePrefill = (idx: number) => {
      isDirty.current = true;
      const newMatchRule = _.cloneDeep(matchRule);
      newMatchRule.prefills.splice(idx, 1);
      setMatchRule(newMatchRule);
    };

    const onSave = async () => {
      if (!matchRule) return;
      const newErrors: Record<string, string> = {};
      if (!matchRule.name) {
        newErrors.name = 'Name is required';
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
      try {
        let saved;
        if (matchRule.id) {
          saved = await updateMatchRule(matchRule).unwrap();
        } else {
          saved = await createMatchRule(matchRule).unwrap();
        }
        setMatchRule(_.cloneDeep(saved.record));
        dispatch(setMatchRuleAction(saved.record));
        isDirty.current = false;
        toast({ type: 'success', title: 'MatchRule saved' });
        if (saved.needReloadAll) {
          refetchAll();
        }
      } catch (e: any) {
        toast({
          type: 'error',
          title: 'Failed to save matchRule',
          message: e.message || e.error,
        });
      }
    };

    const onDelete = async () => {
      await deleteMatchRule(matchRule.id).unwrap();
      toast({ type: 'success', title: 'MatchRule deleted' });
      refetchAll();
    };

    const headerRight = (
      <div className="row gap-2">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          title="Duplicate"
          onClick={onDuplicate}
        >
          <Icon name="copy" size={15} />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          title="Create New"
          onClick={onCreate}
        >
          <Icon name="plus" size={15} />
        </Button>
        <Switch
          size="sm"
          checked={!matchRule.disabled}
          onChange={(checked) => onChange('disabled', !checked)}
        />
      </div>
    );

    return (
      <Panel
        className="grow"
        title={matchRule.id ? 'Edit' : 'Create'}
        icon="edit"
        scroll
        headerRight={headerRight}
        footer={
          <div className="col gap-2">
            <Button
              variant="primary"
              leftIcon="save"
              className="btn-block"
              loading={isCreating || isUpdating}
              disabled={!isDirty.current}
              onClick={onSave}
            >
              Save
            </Button>
            {matchRule.id > 0 && (
              <div className="row gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon="target"
                  className="grow"
                  onClick={onTestMatchRule}
                >
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  title="Delete rule"
                  loading={isDeleting}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this matchRule? This action is irreversible.'
                      )
                    ) {
                      onDelete();
                    }
                  }}
                  style={{ color: 'var(--red-text)' }}
                >
                  <Icon name="trash" size={15} />
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className="col gap-3">
          {/* Sequence */}
          <Field label="Sequence">
            <Input
              size="sm"
              value={matchRule.sequence || ''}
              type="number"
              onChange={(e) => onChange('sequence', Number(e.target.value))}
              placeholder="1, 2, 3…"
            />
          </Field>

          {/* Name */}
          <Field label="Name" hint={errors.name}>
            <Input
              size="sm"
              value={matchRule.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="use_underscores_and_lowercase"
              style={
                errors.name ? { borderColor: 'var(--red-solid)' } : undefined
              }
            />
          </Field>

          {/* Type */}
          <Field label="Type">
            <Select
              size="sm"
              value={matchRule.type}
              onChange={(e) => onChange('type', Number(e.target.value))}
            >
              {Object.values(MATCH_RULE_TYPE)
                .filter((v) => typeof v === 'number')
                .map((type) => (
                  <option key={type} value={type as number}>
                    {MATCH_RULE_TYPE_STR[type as number]}
                  </option>
                ))}
            </Select>
          </Field>

          {/* Weights */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Weights
            </div>
            <div className="row gap-3">
              <Field label="Male (%)" htmlFor="weight-male">
                <Input
                  id="weight-male"
                  size="sm"
                  type="number"
                  value={matchRule.weightMale}
                  onChange={(e) =>
                    onChange('weightMale', Number(e.target.value))
                  }
                  placeholder="100"
                />
              </Field>
              <Field label="Female (%)" htmlFor="weight-female">
                <Input
                  id="weight-female"
                  size="sm"
                  type="number"
                  value={matchRule.weightFemale}
                  onChange={(e) =>
                    onChange('weightFemale', Number(e.target.value))
                  }
                  placeholder="100"
                />
              </Field>
            </div>
          </div>

          {/* Prefill Profile */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Prefill Profile
            </div>
            <div className="col gap-2">
              {matchRule.prefills?.map((field, idx) => (
                <div
                  key={idx}
                  className="row gap-2"
                  style={{
                    background: idx % 2 ? 'var(--bg-muted)' : undefined,
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px 6px',
                  }}
                >
                  <div className="grow">
                    <ProfileFieldSelector
                      value={field}
                      onChange={(value) => onChangePrefill(idx, value)}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    title="Remove"
                    onClick={() => onDeletePrefill(idx)}
                  >
                    <Icon name="x" size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                leftIcon="plus"
                className="btn-block"
                onClick={() =>
                  onChangePrefill(matchRule.prefills?.length || 0, '')
                }
                style={{ marginTop: 4 }}
              >
                Add Prefill
              </Button>
            </div>
          </div>

          {/* Compare Questions */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Compare Questions
            </div>
            <div className="col gap-2">
              {matchRule.fields?.map(([id, field], idx) => (
                <div
                  key={idx}
                  style={{
                    background: idx % 2 ? 'var(--bg-muted)' : undefined,
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px 6px',
                  }}
                >
                  <InterviewCollectSelector
                    id={id}
                    field={field}
                    onChange={(fieldId, value) =>
                      onChangeField(idx, fieldId, value)
                    }
                    onDelete={() => onDeleteField(idx)}
                    language={language}
                  />
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                leftIcon="plus"
                className="btn-block"
                onClick={() => onChangeField(matchRule.fields?.length || 0, 0)}
                style={{ marginTop: 4 }}
              >
                Add Question
              </Button>
            </div>
          </div>

          {/* Additional Instruction */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <Field label="Additional Instruction">
              <Textarea
                value={matchRule.prompt}
                onChange={(e) => onChange('prompt', e.target.value)}
                placeholder="(Optional) How AI should match for these questions"
                rows={3}
              />
            </Field>
          </div>

          {/* Grading */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Grading
            </div>
            <div className="col gap-2">
              {matchRule.grading?.map(([grade, description], idx) => (
                <div
                  key={idx}
                  className="row gap-2"
                  style={{
                    background: idx % 2 ? 'var(--bg-muted)' : undefined,
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px 6px',
                    alignItems: 'center',
                  }}
                >
                  <Input
                    size="sm"
                    className="grow"
                    value={grade}
                    onChange={(e) =>
                      onChangeGrading(idx, e.target.value, description)
                    }
                    placeholder="perfect_match"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    title="Remove"
                    onClick={() => onDeleteGrading(idx)}
                  >
                    <Icon name="x" size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                leftIcon="plus"
                className="btn-block"
                onClick={() =>
                  onChangeGrading(matchRule.grading?.length || 0, '')
                }
                style={{ marginTop: 4 }}
              >
                Add Grade
              </Button>
            </div>
          </div>
        </div>
      </Panel>
    );
  }
);

export default Editor;
